import * as api from '@owl-nest/api-client/next'
import { getCookie } from '@owl-nest/cookie-helper'
import { Either } from '@owl-nest/monad'
import * as dateUtil from '@owl-nest/date'
import * as env from '@owl-nest/config'

export type SignupUser = {
  password: string
  email: string
  lang?: string
  username?: string
}

export type LoginUser = {
  username: string
  password: string
  'stay-connected': boolean
}

export type Tokens = { refresh_token: string; access_token: string }

type ExtendedUser<EXTRA_FIELDS extends string> = api.AuthenticatedUser<EXTRA_FIELDS> & {
  extraFields: EXTRA_FIELDS[]
}

export async function userExists(
  type: 'email' | 'username',
  identifier: string,
): Promise<api.HttpResult<'ulule' | 'facebook' | undefined>> {
  const response = await api.request(env.SIGNUP_URL, {
    method: 'POST',
    type: 'urlencoded',
    body: {
      [type]: identifier,
      dry_run: true,
      // Random password to pass the password validation API-side
      password: '%JVbgkcM8yDUVCgoVzCUxtPE',
      csrfmiddlewaretoken: getCookie('csrftoken'),
    },
  })

  return response.caseOf<api.HttpResult<'ulule' | 'facebook' | undefined>>({
    left: (failure) => {
      if (failure.body?.type === 'APIError') {
        failure = unwrapDjangoError(failure)
      }
      if (isAlreadyExistsError(failure)) {
        if (isUnusablePasswordError(failure)) {
          return Either.right('facebook')
        }
        return Either.right('ulule')
      }
      return Either.left({ ...failure, bypassStatusAutomaticActions: true })
    },
    right: (_) => Either.right(undefined),
  })
}

export async function currentUser<EXTRA_FIELDS extends string = ''>(
  doNotRedirectToSignIn = true,
  extraFields: EXTRA_FIELDS[] = [],
  tokens?: Tokens,
): Promise<api.HttpResult<ExtendedUser<EXTRA_FIELDS>>> {
  const response = await api.client('GET /me', {
    getParams: { extra_fields: extraFields.join(',') },
    getAccessToken() {
      return tokens?.access_token
    },
    getRefreshToken() {
      return tokens?.refresh_token
    },
  })

  return response.caseOf({
    left: (failure) => {
      return Either.left({ ...failure, doNotRedirectToSignIn })
    },
    right: (success) => {
      const authenticatedUser = success.body as api.AuthenticatedUser<EXTRA_FIELDS>
      return Either.right({ ...authenticatedUser, extraFields })
    },
  })
}

export async function signup(signup: SignupUser): Promise<api.HttpResult<api.AuthenticatedUser>> {
  const response = await api.request<api.AuthenticatedUser>(env.SIGNUP_URL, {
    method: 'POST',
    type: 'urlencoded',
    credentials: 'include',
    body: {
      ...signup,
      csrfmiddlewaretoken: getCookie('csrftoken'),
    },
  })

  return response.caseOf<api.HttpResult<api.AuthenticatedUser>>({
    left: (failure) => {
      if (failure.body?.type === 'APIError') {
        failure = unwrapDjangoError(failure)
      }
      if (isAlreadyExistsError(failure)) {
        return Either.left({ ...failure, isAlreadyExistsError: true })
      }
      return Either.left({ ...failure, bypassStatusAutomaticActions: true })
    },
    right: (success) => {
      const user = success.body
      markUserKnown(user)
      return Either.right(user)
    },
  })
}

// Local opt-in to server-side (v2) opaque OAuth tokens: the signin page
// can be loaded with ?token_version=2 and the parameter is forwarded in
// the login POST body (Django also activates v2 through a waffle flag).
function getTokenVersion(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }
  return new URLSearchParams(window.location.search).get('token_version') ?? undefined
}

export async function login<EXTRA_FIELDS extends string = ''>(
  login: LoginUser,
  doNotRedirectToSignIn = true,
  extraFields: EXTRA_FIELDS[] = [],
): Promise<api.HttpResult<ExtendedUser<EXTRA_FIELDS> & Tokens>> {
  const tokenVersion = getTokenVersion()
  const response = await api.request<{ access_token: string; refresh_token: string; expires_in: number }>(
    env.SIGNIN_URL,
    {
      method: 'POST',
      type: 'urlencoded',
      credentials: 'include',
      body: {
        ...login,
        ...(tokenVersion === '2' && { token_version: tokenVersion }),
        csrfmiddlewaretoken: getCookie('csrftoken'),
      },
    },
  )

  const userResponse = await response.caseOf<Promise<api.HttpResult<ExtendedUser<EXTRA_FIELDS> & Tokens>>>({
    left: async (failure) => {
      if (failure.body?.type === 'APIError') {
        failure = unwrapDjangoError(failure)
      }
      return Either.left({ ...failure, bypassStatusAutomaticActions: true })
    },
    right: async (success) => {
      return (
        await currentUser(doNotRedirectToSignIn, extraFields, {
          refresh_token: success.body.refresh_token,
          access_token: success.body.access_token,
        })
      ).next((user) => ({
        ...user,
        refresh_token: success.body.refresh_token,
        access_token: success.body.access_token,
      }))
    },
  })

  return userResponse.next((user) => {
    markUserKnown(user)
    return user
  })
}

export async function guestLogin<EXTRA_FIELDS extends string = ''>(
  email: string,
  doNotRedirectToSignIn = true,
  extraFields: EXTRA_FIELDS[] = [],
): Promise<api.HttpResult<ExtendedUser<EXTRA_FIELDS>>> {
  const tokenVersion = getTokenVersion()
  const response = await api.request<{ access_token: string; refresh_token: string; expires_in: number }>(
    env.SIGNIN_URL,
    {
      method: 'POST',
      type: 'urlencoded',
      credentials: 'include',
      body: {
        csrfmiddlewaretoken: getCookie('csrftoken'),
        email,
        guest: 1,
        ...(tokenVersion === '2' && { token_version: tokenVersion }),
      },
    },
  )

  const userResponse = await response.caseOf<Promise<api.HttpResult<ExtendedUser<EXTRA_FIELDS>>>>({
    left: async (failure) => {
      if (failure.body?.type === 'APIError') {
        failure = unwrapDjangoError(failure)
      }
      return Either.left({ ...failure, bypassStatusAutomaticActions: true })
    },
    right: async (_success) => {
      return await currentUser(doNotRedirectToSignIn, extraFields)
    },
  })

  return userResponse.next((user) => {
    return user
  })
}

export async function logout(): Promise<api.HttpResult<void>> {
  const response = await api.request(env.SIGNOUT_URL, {
    method: 'GET',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  return response.next((_) => {
    /* Nothing to pass in case of success */
  })
}

export type KnownUser = api.PublicUser & { service: 'ulule' | 'facebook' }

export async function userKnown(): Promise<KnownUser | undefined> {
  if (typeof document === 'undefined') {
    return undefined
  }

  const lastUserItem = window.localStorage.getItem('ul_last_user')
  if (lastUserItem === null) {
    return undefined
  } else {
    try {
      const { username, expires } = JSON.parse(lastUserItem)

      if (expires < Date.now()) {
        forgetUser()
        return undefined
      }

      const service = (await userExists('username', username)).doThrow()

      if (service === undefined) {
        return undefined
      }

      const { body: user } = (
        await api.client('GET /users/:id', {
          urlParams: { id: username },
        })
      ).doThrow()

      return {
        ...user,
        service,
      }
    } catch (error) {
      return undefined
    }
  }
}

export function markUserKnown<E extends string>(user: api.AuthenticatedUser<E>): void {
  if (typeof document !== 'undefined') {
    window.localStorage.setItem(
      'ul_last_user',
      JSON.stringify({
        username: user.username,
        expires: dateUtil.daysFromNow(7 * 3),
      }),
    )
  }
}

export function forgetUser(): void {
  if (typeof document !== 'undefined') {
    window.localStorage.removeItem('ul_last_user')
  }
}

//FIXME: this should not be here, it is not related to authentication
export async function update(
  userId: number,
  user: api.UpdateUser,
  token?: string,
): Promise<api.HttpResult<api.AuthenticatedUser>> {
  const response = await api.client('PATCH /users/:id', {
    urlParams: { id: String(userId) },
    body: user,
    token,
  })

  return response.next((success) => {
    return success.body
  })
}

function unwrapDjangoError(failure: api.HttpFailure): api.HttpFailure {
  return {
    ...failure,
    body: failure.body.errors,
  }
}

export function isAlreadyExistsError(failure: api.HttpFailure): boolean {
  if (failure.status === 422) {
    const apiError = failure.body as api.ApiError
    for (const error of apiError) {
      if (error.classification === 'AlreadyExistsError') {
        return true
      }
    }
  }
  return false
}

export function isUnusablePasswordError(failure: api.HttpFailure): boolean {
  if (failure.status === 422) {
    const apiError = failure.body as api.ApiError
    for (const error of apiError) {
      if (error.classification === 'UnusablePasswordError') {
        return true
      }
    }
  }
  return false
}
